import React, { useState } from 'react';
import * as styles from './EmailAddressSection.module.scss';
import { Inline, Stack } from 'components/layout';
import {
  Badge,
  Button,
  Heading,
  IconList,
  StructuredList,
  StructuredListItem,
  Text,
  Toggletip,
} from 'components/core';

type Email = {
  email: string;
  created_at: string;
  verified_at?: string;
  primary: boolean;
};

export type EmailAddressSectionProps = {
  emails: Email[];
  makePrimary: (email: string) => Promise<void>;
  removeEmail: (email: string) => void;
  resendVerification: (email: string) => Promise<void>;
  addEmail: () => void;
};

const PrimaryEmail = (props: {
  email: Email;
  inProgress: InProgress;
  onResendVerification: () => void;
}) => {
  const { email, inProgress, onResendVerification } = props;

  const verified = !!email.verified_at;

  return (
    <StructuredListItem icon={null}>
      <Inline spacing={2}>
        <div className={styles.email}>{email.email}</div>
        <BadgeCluster verified={verified} primary={true} />
      </Inline>

      <Inline spacing={2}>
        {verified ? (
          <Toggletip
            idPrefix="unable-to-remove"
            triggerText="Unable to remove"
            triggerVariant="weak"
          >
            Primary email cannot be removed. If you want to remove this email,
            add another email as primary first.
          </Toggletip>
        ) : (
          <Button
            onClick={onResendVerification}
            type="neutral"
            variant="ghost"
            aria-label={`Resend verification email for ${email.email}`}
            isLoading={inProgress === 'resend-verification'}
            disabled={!!inProgress}
          >
            Resend verification email
          </Button>
        )}
      </Inline>
    </StructuredListItem>
  );
};
const BadgeCluster = (props: { verified: boolean; primary: boolean }) => {
  const { verified, primary } = props;
  return (
    <Inline className={styles.badgeCluster} alignY="center" spacing={2}>
      {primary && <Badge variant="solid">Primary</Badge>}
      {!verified && (
        <>
          <Badge type="neutral" variant="outline">
            Unverified
          </Badge>
          <Toggletip
            idPrefix="email-unverified"
            icon={IconList.light.faCircleInfo}
            triggerVariant="weak"
          >
            Unverified email addresses cannot receive notifications or be used
            to reset your password.
          </Toggletip>
        </>
      )}
    </Inline>
  );
};

type EmailItemProps = {
  verified: boolean;
  email: Email;
  makePrimary: EmailAddressSectionProps['makePrimary'];
  resendVerification: EmailAddressSectionProps['resendVerification'];
  removeEmail: EmailAddressSectionProps['removeEmail'];
};

type InProgress =
  | 'make-primary'
  | 'remove-email'
  | 'resend-verification'
  | null;

const EmailItem = (props: EmailItemProps) => {
  const {
    email,
    verified,
    resendVerification,
    removeEmail,
    makePrimary,
  } = props;

  const [inProgress, setInProgress] = useState<InProgress>(null);

  if (email.primary) {
    return (
      <PrimaryEmail
        email={email}
        key={email.email}
        onResendVerification={async () => {
          setInProgress('resend-verification');
          await resendVerification(email.email);
          setInProgress(null);
        }}
        inProgress={inProgress}
      />
    );
  }

  return (
    <StructuredListItem icon={null} key={email.email}>
      <Inline spacing={2}>
        <Text className={styles.emailText}>{email.email} </Text>
        {!verified && (
          <BadgeCluster verified={verified} primary={email.primary} />
        )}
      </Inline>
      <Inline spacing={2}>
        {verified ? (
          <Button
            onClick={async () => {
              setInProgress('make-primary');
              await makePrimary(email.email);
              setInProgress(null);
            }}
            type="neutral"
            variant="ghost"
            aria-label={`Make ${email.email} the primary email`}
            isLoading={inProgress === 'make-primary'}
            disabled={!!inProgress}
          >
            Make primary
          </Button>
        ) : (
          <Button
            onClick={async () => {
              setInProgress('resend-verification');
              await resendVerification(email.email);
              setInProgress(null);
            }}
            type="neutral"
            variant="ghost"
            aria-label={`Resend verification email for ${email.email}`}
            isLoading={inProgress === 'resend-verification'}
            disabled={!!inProgress}
          >
            Resend verification email
          </Button>
        )}
        <Button
          onClick={() => {
            removeEmail(email.email);
          }}
          aria-label={`Remove email ${email.email}`}
          variant="outline"
          isLoading={inProgress === 'remove-email'}
          disabled={!!inProgress}
        >
          Remove email
        </Button>
      </Inline>
    </StructuredListItem>
  );
};
const EmailAddressSection = (props: EmailAddressSectionProps) => {
  const {
    emails,
    makePrimary,
    removeEmail,
    resendVerification,
    addEmail,
  } = props;

  const primaryEmail = emails.find((email) => email.primary);

  const primaryVerified = !!primaryEmail?.verified_at;

  return (
    <Stack spacing={3}>
      <Heading type="h4">Email addresses</Heading>
      <StructuredList>
        {emails.map((email) => {
          return (
            <EmailItem
              makePrimary={makePrimary}
              removeEmail={removeEmail}
              resendVerification={resendVerification}
              email={email}
              verified={!!email.verified_at}
              key={email.email}
            />
          );
        })}
      </StructuredList>
      <div>
        {primaryVerified ? (
          <Button
            className={styles.addNewEmail}
            onClick={addEmail}
            type="brand"
          >
            Add new email
          </Button>
        ) : (
          <Text>
            Please verify your primary email address before adding new emails
          </Text>
        )}
      </div>
    </Stack>
  );
};

export { EmailAddressSection };
